import React from "react";
import type { TowerShipment } from "../../types/controlTower";

// Tortas por zona del Resumen: cómo va cada zona (entregados, en reparto,
// pendientes, incidencias y otros) y un atajo a sus alertas.
// Las zonas van en tono neutro y se distinguen por el texto.
const ZONES = ["NORTE", "SUR", "CABA", "OESTE"] as const;
// Mismos colores de estado que los marcadores del mapa.
const PARTS = [
  { key: "delivered", label: "Entregados", color: "#48bd8d" },
  { key: "inTransit", label: "En reparto", color: "#e5ad3f" },
  { key: "pending", label: "Pendientes", color: "#5a9cff" },
  { key: "incident", label: "Incidencias", color: "#df6666" },
  { key: "other", label: "Otros", color: "#a7a7ad" },
] as const;
type PartKey = (typeof PARTS)[number]["key"];

function partOf(s: TowerShipment): PartKey {
  if (s.status === "delivered" || s.status === "inTransit" || s.status === "pending" || s.status === "incident")
    return s.status;
  return "other";
}

export type ZoneAlertCounts = { inactivity: number; geo: number };

const RADIUS = 44;
const STROKE = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// Separación entre porciones, del color de la superficie.
const GAP = 2;

function Donut({ zone, counts, total }: { zone: string; counts: Record<PartKey, number>; total: number }) {
  const delivered = total ? Math.round((counts.delivered / total) * 100) : 0;
  let offset = 0;
  const visible = PARTS.filter((part) => counts[part.key] > 0);
  return (
    <svg viewBox="0 0 120 120" width="112" height="112" role="img" aria-label={`Zona ${zone}: ${delivered}% entregado`}>
      <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="var(--surface-alt)" strokeWidth={STROKE} />
      {visible.map((part) => {
        const length = (counts[part.key] / total) * CIRCUMFERENCE;
        const dash = Math.max(length - (visible.length > 1 ? GAP : 0), 0.5);
        const segment = (
          <circle
            key={part.key}
            cx="60"
            cy="60"
            r={RADIUS}
            fill="none"
            stroke={part.color}
            strokeWidth={STROKE}
            strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 60 60)"
          >
            <title>{`${part.label}: ${counts[part.key]} de ${total}`}</title>
          </circle>
        );
        offset += length;
        return segment;
      })}
      <text x="60" y="58" textAnchor="middle" className="tower-donut-value">
        {total ? `${delivered}%` : "—"}
      </text>
      <text x="60" y="74" textAnchor="middle" className="tower-donut-caption">
        entregado
      </text>
    </svg>
  );
}

export default function TowerZoneCharts({
  shipments,
  alerts,
  onOpenMonitor,
  onOpenGeo,
}: {
  // Envíos de choferes del corte elegido (sin depósito).
  shipments: TowerShipment[];
  // Alertas por zona; null cuando no hay alertas que mostrar (otro día).
  alerts: Record<string, ZoneAlertCounts> | null;
  onOpenMonitor: () => void;
  onOpenGeo: () => void;
}) {
  return (
    <section className="tower-zone-charts" aria-label="Avance por zona">
      {ZONES.map((zone) => {
        const zoneShipments = shipments.filter((s) => s.zone === zone);
        const counts = Object.fromEntries(PARTS.map((part) => [part.key, 0])) as Record<PartKey, number>;
        for (const s of zoneShipments) counts[partOf(s)]++;
        const zoneAlerts = alerts?.[zone];
        return (
          <article key={zone} className="tower-zone-chart">
            <header>
              <strong>
                <span className="tower-zone-dot" />
                Zona {zone}
              </strong>
              <span className="tower-muted">{zoneShipments.length} envíos</span>
            </header>
            <div className="tower-zone-chart-body">
              <Donut zone={zone} counts={counts} total={zoneShipments.length} />
              <ul className="tower-zone-chart-legend">
                {PARTS.map((part) => (
                  <li key={part.key}>
                    <i style={{ background: part.color }} aria-hidden="true" />
                    {part.label}
                    <b>{counts[part.key]}</b>
                  </li>
                ))}
              </ul>
            </div>
            {zoneAlerts && (
              <footer className="tower-zone-chart-alerts">
                <button
                  type="button"
                  className={zoneAlerts.inactivity ? "has-alerts" : undefined}
                  onClick={onOpenMonitor}
                  title="Choferes de la zona con alerta de inactividad. Abre Monitoreo."
                >
                  {zoneAlerts.inactivity} de inactividad
                </button>
                <button
                  type="button"
                  className={zoneAlerts.geo ? "has-alerts" : undefined}
                  onClick={onOpenGeo}
                  title="Envíos de la zona lejos del reparto de su chofer. Abre Alertas geográficas."
                >
                  {zoneAlerts.geo} geográficas
                </button>
              </footer>
            )}
          </article>
        );
      })}
    </section>
  );
}
